import { useEffect, useState } from "react";
import { TrendingUp, Loader2, FileUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function ValuationRequestDialog({ companyId, currentValuation, isAdmin, open, onOpenChange, onDone }) {
  const [valuation, setValuation] = useState(currentValuation ? String(currentValuation) : "");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValuation(currentValuation ? String(currentValuation) : "");
      setNote("");
      setFile(null);
    }
  }, [open, currentValuation]);

  const submit = async () => {
    const num = Number(valuation);
    if (Number.isNaN(num) || num < 0) return toast.error("Skriv inn et gyldig beløp i kroner");
    const fd = new FormData();
    fd.append("valuation", String(num));
    fd.append("note", note || "");
    if (file) fd.append("file", file);
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/valuation-request`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.status === "approved") toast.success("Verdsettelse oppdatert");
      else toast.success("Forslag med dokumentasjon sendt til Murphy for godkjenning");
      onOpenChange(false);
      setNote("");
      setFile(null);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="valuation-request-dialog">
        <DialogHeader>
          <DialogTitle>{isAdmin ? "Oppdater verdsettelse" : "Foreslå ny verdsettelse"}</DialogTitle>
          <DialogDescription>
            {isAdmin
              ? "Sett verdsettelsen manuelt. Du kan legge ved en begrunnelse og dokumentasjon."
              : "Foreslå en verdsettelse med begrunnelse og dokumentasjon. Murphy (plattform-admin) godkjenner forslaget."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Verdsettelse (kr)</Label>
            <Input
              type="number"
              inputMode="numeric"
              className="mt-1.5"
              data-testid="valuation-request-amount"
              value={valuation}
              onChange={(e) => setValuation(e.target.value)}
              placeholder="f.eks. 5 000 000"
              autoFocus
            />
          </div>
          <div>
            <Label>Begrunnelse / kommentar</Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              data-testid="valuation-request-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Hvorfor foreslås denne verdsettelsen? (grunnlag, metode, referanser)"
            />
          </div>
          <div>
            <Label>Dokumentasjon (valgfritt)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm hover:bg-muted/40" data-testid="valuation-file-label">
              <FileUp className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Velg fil (PDF, bilde eller dokument) …"}</span>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*"
                className="hidden"
                data-testid="valuation-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={saving} className="gap-2" data-testid="valuation-request-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
            {isAdmin ? "Lagre verdsettelse" : "Send til godkjenning"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
